import {
  Box,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Skeleton,
  Typography
} from '@mui/material'

import { AssistantSkill } from '../../../generated/graphql'

export type SkillSlashMenuProps = {
  // Pre-ranked skills computed by the parent (which already needs the same
  // ranking for keyboard navigation). Avoids duplicating the rankSkills call
  // between the parent and this component.
  rankedSkills: AssistantSkill[]
  activeIndex: number
  onActiveIndexChange: (i: number) => void
  onSelect: (skill: AssistantSkill) => void
  onClose: () => void
  anchorEl: HTMLElement | null
  loading: boolean
  error: boolean
}

// Visible-row sizing: ~3 rows visible before the menu starts to scroll.
// Each MenuItem with primary + secondary text is roughly 56px tall in MUI v5.
const VISIBLE_ROWS = 3
const ROW_HEIGHT_PX = 56
const MAX_HEIGHT_PX = VISIBLE_ROWS * ROW_HEIGHT_PX

const ROOT_TEST_ID = 'skill-slash-menu'
const ROW_TEST_ID = 'skill-slash-menu-row'
const ROW_NAME_TEST_ID = 'skill-slash-menu-row-name'
const ROW_DESC_TEST_ID = 'skill-slash-menu-row-description'
const EMPTY_TEST_ID = 'skill-slash-menu-empty'
const ERROR_TEST_ID = 'skill-slash-menu-error'
const LOADING_TEST_ID = 'skill-slash-menu-loading'

// Stable DOM ids used by the parent's combobox aria-controls /
// aria-activedescendant. Keep in sync with whatever the textbox spreads.
export const SKILL_SLASH_MENU_LISTBOX_ID = 'skill-slash-menu-listbox'
export const skillSlashMenuOptionId = (index: number): string => `skill-slash-menu-option-${index}`

/**
 * Take the first sentence of a description for compact menu rendering.
 *
 * Splits on the first sentence-terminating punctuation (`.`, `!`, `?`)
 * followed by whitespace. Returns the original string when no terminator is
 * found.
 */
const firstSentence = (text: string): string => {
  const match = text.match(/^(.+?[.!?])(\s|$)/)
  return match ? match[1] : text
}

/**
 * Floating slash menu that lists `AssistantSkill` rows ranked against the
 * current slash query. Pure presentational — the parent owns active-index
 * state so keyboard navigation (driven from the input) can stay in sync.
 *
 * Wraps an MUI `Popper` anchored above the chat input via `top-start`. A
 * `ClickAwayListener` translates outside clicks into `onClose`. When in an
 * error or loading-without-data state, the menu shows a single non-interactive
 * row and ignores selection clicks.
 */
const SkillSlashMenu = (props: SkillSlashMenuProps) => {
  const {
    rankedSkills,
    activeIndex,
    onActiveIndexChange,
    onSelect,
    onClose,
    anchorEl,
    loading,
    error
  } = props

  const showError = error
  // Show loading only while the query is in flight AND we have nothing yet.
  // Once skills resolve and the query filters everything out, fall through to
  // the empty state instead of pretending we're still loading.
  const showLoading = !error && loading && rankedSkills.length === 0
  const showEmpty = !error && !showLoading && rankedSkills.length === 0

  return (
    <Popper
      open={anchorEl !== null}
      anchorEl={anchorEl}
      placement="top-start"
      data-testid={ROOT_TEST_ID}
      data-placement="top-start"
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose} mouseEvent="onMouseDown">
        <Paper elevation={4} sx={{ minWidth: 280, maxWidth: 420 }}>
          <MenuList
            dense
            autoFocusItem={false}
            id={SKILL_SLASH_MENU_LISTBOX_ID}
            role="listbox"
            aria-label="Assistant skills"
            sx={{
              maxHeight: MAX_HEIGHT_PX,
              overflowY: 'auto',
              py: 0
            }}
          >
            {showError && (
              <MenuItem
                disabled
                role="presentation"
                data-testid={ERROR_TEST_ID}
                sx={{ pointerEvents: 'none' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Couldn&apos;t load skills
                </Typography>
              </MenuItem>
            )}

            {showLoading && (
              <MenuItem
                disabled
                role="presentation"
                data-testid={LOADING_TEST_ID}
                sx={{ pointerEvents: 'none' }}
              >
                <Box sx={{ width: '100%' }}>
                  <Skeleton variant="text" width="50%" />
                  <Skeleton variant="text" width="80%" />
                </Box>
              </MenuItem>
            )}

            {showEmpty && (
              <MenuItem
                disabled
                role="presentation"
                data-testid={EMPTY_TEST_ID}
                sx={{ pointerEvents: 'none' }}
              >
                <Typography variant="body2" color="text.secondary">
                  No matching skills
                </Typography>
              </MenuItem>
            )}

            {!showError &&
              !showLoading &&
              rankedSkills.map((skill, index) => (
                <MenuItem
                  key={skill.name}
                  id={skillSlashMenuOptionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  data-testid={ROW_TEST_ID}
                  selected={index === activeIndex}
                  onMouseEnter={() => onActiveIndexChange(index)}
                  onClick={() => onSelect(skill)}
                  sx={{ alignItems: 'flex-start', py: 0.75 }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      data-testid={ROW_NAME_TEST_ID}
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                    >
                      {skill.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      data-testid={ROW_DESC_TEST_ID}
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {firstSentence(skill.description)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

export default SkillSlashMenu
